import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from 'src/components/UI/Modal';
import {
  usePatchOrderByIdMutation,
  useUnlockOrderMutation,
} from 'src/services/orders/orders';
import {
  DEFAULT_ORDER,
  Driver,
  Escort,
  Order,
  Vehicle,
} from 'src/utils/interfaces/interfaces';
import {
  APPROVED,
  COMPLETED,
  IN_TRANSIT,
  STATUS,
} from 'src/constants/statusConstants';
import { getUser, getUserType } from 'src/utils/user/user';
import { UAEStatesMap } from 'src/data/States';
import {
  Avatar,
  Button,
  Chip,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Modal as NextModal,
  Select,
  SelectItem,
} from '@heroui/react';
import CustomSnackbar from 'src/components/UI/Alerts/customSnackbar';

export interface IJobDetailsProps {
  Order: Order;
  presetData?: any;
  Driver?: any;
  refetchOrder?: () => void;
}

function BatteryProgress({
  value,
  level,
}: {
  value: number;
  level: number | undefined;
}) {
  const fillColor =
    level === 255
      ? 'var(--color-primary)'
      : level !== undefined && level < 80
        ? 'var(--color-error)'
        : 'var(--color-primary)';

  return (
    <div
      className="h-5 w-[120px] overflow-hidden rounded-[5px]"
      style={{ backgroundColor: 'var(--bg-surface-2)' }}
    >
      <div
        className="h-full rounded-[5px] transition-all duration-300"
        style={{
          width: `${Math.max(0, Math.min(100, value))}%`,
          backgroundColor: fillColor,
        }}
      />
    </div>
  );
}

export function JobDetails({
  Order = DEFAULT_ORDER,
  refetchOrder,
  presetData,
}: IJobDetailsProps) {
  const { t } = useTranslation();
  const { id } = useParams();

  // ====== Queries / Mutations ======
  const [
    patchOrderById,
    { isError, isSuccess, error: patchError },
  ] = usePatchOrderByIdMutation();
  const [unlockOrder] = useUnlockOrderMutation();

  // ====== Local state ======
  const user = getUser();
  const userType = getUserType();
  const [showUnlock, setShowUnlock] = useState(false);

  const [chargingProgress, setChargingProgress] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Selected entities
  const [selectedDriver, setSelectedDriver] = useState<Driver | undefined>();
  const [selectedEscort, setSelectedEscort] = useState<Escort | undefined>();
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | undefined>();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('success');

  // ====== Effects ======
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (Order?.batteryLevel === 255) {
      interval = setInterval(() => {
        setChargingProgress((prevProgress) =>
          prevProgress >= 100 ? 0 : prevProgress + 10
        );
      }, 400);
    }
    return () => {
      clearInterval(interval);
    };
  }, [Order?.batteryLevel]);

  // ====== Handlers ======
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDriver(undefined);
    setSelectedEscort(undefined);
    setSelectedVehicle(undefined);
  };

  const handleUnlockOrder = () => {
    if (!Order.imei) return;
    unlockOrder({ imei: Order.imei })
      .unwrap()
      .then((response: any) => {
        setShowUnlock(false);
        setOpenSnackbar(true);
        setSnackbarMessage(response?.message || 'Unlock command sent');
        setSnackbarSeverity('success');
        refetchOrder?.();
      })
      .catch((response: any) => {
        setShowUnlock(false);
        setOpenSnackbar(true);
        setSnackbarMessage(response?.data?.message || 'Failed to unlock order');
        setSnackbarSeverity('error');
      });
  };

  useEffect(() => {
    if (isSuccess) {
      setOpenSnackbar(true);
      setSnackbarMessage('Updated successfully!');
      setSnackbarSeverity('success');
      refetchOrder?.();
      closeModal();
    }

    if (isError && patchError) {
      closeModal();
      const msg = (patchError as any)?.data?.message;
      setOpenSnackbar(true);
      setSnackbarMessage(msg ?? t("Failed to update, Please try again!"));
      setSnackbarSeverity('error');
      setSelectedDriver(undefined)
      setSelectedEscort(undefined)
      setSelectedVehicle(undefined)
    }
  }, [isSuccess, isError, patchError]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const handleConfirmTransportDetails = () => {
    const payload: Record<string, unknown> = {
      status: 'ready',
    };

    if (selectedDriver) payload.driver = selectedDriver._id;
    if (selectedEscort) payload.escort = selectedEscort._id;
    if (selectedVehicle) payload.vehicle = selectedVehicle._id;

    patchOrderById({ id: Order._id, data: payload });
  };

  return (
    <div>
      {/* ============= AGENCY DETAILS ============= */}
      <div className="grid grid-cols-6 px-4 mb-2">
        <div>
          <img
            src="/assets/icons/orders/jobDetails/routing.svg"
            alt="routing"
            className="theme-icon mx-auto"
          />
        </div>
        <div className="col-span-5">
          <p className="text-base font-bold ">
            {t('order_details.agency_details')}
          </p>
        </div>
      </div>
      {/* Agency Name */}
      <div className="grid grid-cols-6 px-4 my-5">
        <div className="place-self-center ">
          <img
            src="/assets/icons/orders/jobDetails/agency_logo.svg"
            alt="agency"
            className="mx-auto"
          />
        </div>
        <div className="col-span-5">
          <p className="text-sm text-[var(--text-muted)]">
            {t('order_details.agency_name')}
          </p>
          <p
            className={`text-sm ${Order?.shippingCompany?.name &&
              Order?.shippingCompany?.name !== 'N/A'
              ? ''
              : 'text-[var(--text-muted)] opacity-70'
              }`}
          >
            {Order?.shippingCompany?.name &&
              Order?.shippingCompany?.name !== 'N/A'
              ? Order?.shippingCompany?.name
              : `${t('order_details.data_not_provided')}`}
          </p>
        </div>
      </div>
      {/* Contact Number */}
      <div className="grid grid-cols-6 px-4 my-5">
        <div className="place-self-center ">
          <img
            src="/assets/icons/orders/jobDetails/phone.svg"
            alt="phone"
            className="theme-icon mx-auto"
          />
        </div>
        <div className="col-span-5">
          <p className="text-sm text-[var(--text-muted)]">
            {t('order_details.contact_number')}
          </p>
          <p
            className={`text-sm ${Order?.shipper?.phone && Order?.shipper?.phone !== 'N/A'
              ? ''
              : 'text-[var(--text-muted)] opacity-70'
              }`}
          >
            {Order?.shipper?.phone && Order?.shipper?.phone !== 'N/A'
              ? Order?.shipper?.phone
              : `${t('order_details.data_not_provided')}`}
          </p>
        </div>
      </div>
      {/* Office Address */}
      <div className="grid grid-cols-6 px-4 my-5">
        <div className="place-self-center ">
          <img
            src="/assets/icons/orders/jobDetails/office.svg"
            alt="office"
            className="theme-icon mx-auto"
          />
        </div>
        <div className="col-span-5">
          <p className="text-sm text-[var(--text-muted)]">
            {t('order_details.office_address')}
          </p>
          <p
            className={`text-sm ${Order?.shippingCompany?.address &&
              Order.shippingCompany.address.street !== 'N/A' &&
              Order.shippingCompany.address.city !== 'N/A' &&
              Order.shippingCompany.address.state !== 'N/A' &&
              Order.shippingCompany.address.country !== 'N/A'
              ? ''
              : 'text-[var(--text-muted)] opacity-70'
              }`}
          >
            {Order?.shippingCompany?.address &&
              Order.shippingCompany.address.street !== 'N/A' &&
              Order.shippingCompany.address.city !== 'N/A' &&
              Order.shippingCompany.address.state !== 'N/A' &&
              Order.shippingCompany.address.country !== 'N/A'
              ? [
                Order.shippingCompany.address.street,
                Order.shippingCompany.address.city,
                Order.shippingCompany.address.state,
                Order.shippingCompany.address.country,
              ]
                .filter((item) => item && item !== 'N/A')
                .join(', ')
              : `${t('order_details.data_not_provided')}`}
          </p>
        </div>
      </div>
      <hr className="opacity-10" />
      {/* ============= ROUTE DETAILS ============= */}
      <div className="grid grid-cols-6 my-5 px-2">
        <div>
          <img
            src="/assets/icons/orders/jobDetails/routing.svg"
            alt="routing"
            className="theme-icon mx-auto"
          />
        </div>
        <div className="col-span-3">
          <p className="text-base font-bold ">
            {t('order_details.route_details')}
          </p>
        </div>
        <div className="col-span-2 mr-4">
          <Chip
            variant="faded"
            size="md"
            className={`py-1 px-2 w-fit ml-auto rounded-full text-white float-right shadow-lg capitalize ${Order.status === COMPLETED ||
              Order.status === 'complete' ||
              Order.status === 'reached' ||
              Order.status === APPROVED
              ? 'bg-[var(--color-primary)]'
              : Order.status === IN_TRANSIT
                ? 'bg-[var(--color-primary-hover)] opacity-90'
                : Order.status === 'ready'
                  ? 'bg-[var(--color-primary)]'
                  : Order.status === 'draft'
                    ? 'bg-[var(--bg-surface-2)] text-[var(--text-muted)]'
                    : Order.status === 'Not started'
                      ? 'bg-[var(--color-primary)]'
                      : 'bg-[var(--bg-surface-2)] text-[var(--text-muted)]'
              }`}
          >
            {STATUS[Order.status ? Order.status : '']?.name}
          </Chip>
        </div>
      </div>
      <div className="grid grid-cols-6 mb-10 px-2">
        <div className="relative">
          <img
            src="/assets/icons/start_icon.svg"
            alt="start"
            className="z-10 mx-auto"
          />
          <img
            src="/assets/icons/line-dotted.svg"
            alt="start"
            className="theme-icon mx-auto line-dotted"
          />
        </div>
        <div className="col-span-5">
          <p className="text-sm text-[var(--text-muted)]">
            {t('order_details.pickup_location')}
          </p>
          <p className="max-w-xs text-sm">{Order?.address?.origin}</p>
          <p className="text-xs text-[var(--text-muted)]">
            {(() => {
              const dateToDisplay = Order?.startedAt || Order?.createdAt;
              return dateToDisplay
                ? moment(dateToDisplay).format('Do MMM YYYY, h:mm a')
                : 'N/A';
            })()}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-6 mb-4 px-2">
        <div>
          <img
            src="/assets/icons/end_icon.svg"
            alt="end"
            className="z-10 mx-auto"
          />
        </div>
        <div className="col-span-5">
          <p className="text-sm text-[var(--text-muted)]">
            {t('order_details.drop_location')}
          </p>
          <p className="max-w-xs text-sm">{Order?.address?.destination}</p>
          {Order?.completedAt && (
            <p className="text-xs text-[var(--text-muted)]">
              {Order.status &&
                Order.status === 'completed' &&
                moment(Order.completedAt).format('Do MMM YYYY, h:mm a')}
            </p>
          )}
        </div>
      </div>
      <hr className="opacity-10" />
      {Order?.status !== 'draft' && Order?.status !== 'ready' && (
        <>
          {/* ============= TRACKER DETAILS ============= */}
          <div className="grid grid-cols-6 my-5 px-2">
            <div className="place-self-center ">
              <img
                src="/assets/icons/orders/jobDetails/cpu.svg"
                alt="transport"
                className="theme-icon mx-auto"
              />
            </div>
            <div className="col-span-5">
              <p className="text-base font-bold ">
                {t('order_details.tracker_details')}
              </p>
            </div>
          </div>
          {/* IMEI */}
          <div className="grid grid-cols-6 mb-4 px-2">
            <div className="place-self-center ">
              <img
                src="/assets/icons/orders/jobDetails/imei.svg"
                alt="driver"
                className="theme-icon mx-auto"
              />
            </div>
            <div className="col-span-5">
              <p className="text-sm text-[var(--text-muted)]">IMEI</p>
              <p
                className={`text-sm ${Order?.imei && Order.imei !== 'N/A'
                  ? ''
                  : 'text-[var(--text-muted)] opacity-70'
                  }`}
              >
                {Order?.imei && Order.imei !== 'N/A'
                  ? Order.imei
                  : `${t('order_details.data_not_provided')}`}
              </p>
            </div>
          </div>
          {/* Battery Status */}
          {Order?.batteryLevel !== undefined && (
            <div className="grid grid-cols-6 mb-4 px-2">
              <div className="place-self-center rtl:rotate-180">
                <img
                  src="/assets/icons/orders/jobDetails/electricity.svg"
                  alt="battery"
                  className="theme-icon mx-auto"
                />
              </div>
              <div className="col-span-5">
                <p className="text-sm text-[var(--text-muted)]">
                  {t('order_details.battery_status')}
                </p>
                <div className="relative flex items-center mt-1">
                  {Order?.batteryLevel === 255 ? (
                    <>
                      <BatteryProgress
                        level={undefined}
                        value={chargingProgress}
                      />
                      <div className="absolute w-full ml-2 text-xs">
                        {t('order_details.lock_charging')}
                      </div>
                    </>
                  ) : (
                    <>
                      <BatteryProgress
                        level={Order?.batteryLevel}
                        value={Order?.batteryLevel}
                      />
                      <p className="ml-4 text-sm">
                        {Order?.batteryLevel}%
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          <hr className="opacity-10" />
        </>
      )}
      {/* ============= SHIPMENT DETAILS ============= */}
      <div className="grid grid-cols-6 my-5 px-2">
        <div className="place-self-center ">
          <img
            src="/assets/icons/orders/jobDetails/container.svg"
            alt="container"
            className="theme-icon mx-auto"
          />
        </div>
        <div className="col-span-5">
          <p className="text-base font-bold ">
            {t('create_order.shipment_details')}
          </p>
        </div>
      </div>
      <div className="overflow-x-hidden px-6 mb-4">
        <table className="min-w-full table-fixed border-collapse text-sm overflow-hidden">
          <thead className="text-[var(--text-main)]" style={{ backgroundColor: "var(--bg-surface-2)" }}>
            <tr>
              <th className="w-1/12 px-4 py-2 border border-[var(--border-light)] text-left">
                {t('create_order.sl_number')}
              </th>
              <th className="w-1/4 px-4 py-2 border border-[var(--border-light)] text-left">
                {t('create_order.description')}
              </th>
              <th className="w-1/6 px-4 py-2 border border-[var(--border-light)] text-left">
                {t('create_order.boxes_qty')}
              </th>
              <th className="w-1/6 px-4 py-2 border border-[var(--border-light)] text-left">
                {t('create_order.gross_weight')}
              </th>
            </tr>
          </thead>
          <tbody>
            {Order?.shipmentDetails?.map((detail, index) => (
              <tr key={detail._id} className="even:bg-[var(--bg-surface)]">
                <td className="px-4 py-2 border border-[var(--border-light)] text-center">
                  {index + 1}
                </td>
                <td
                  className="px-4 py-2 border border-[var(--border-light)] truncate"
                  style={{ maxWidth: '5rem' }}
                  title={detail.description}
                >
                  {detail.description}
                </td>
                <td
                  className="px-4 py-2 border border-[var(--border-light)] truncate"
                  style={{ maxWidth: '5rem' }}
                  title={`${detail.boxesQty ?? ''}`}
                >
                  {detail.boxesQty}
                </td>
                <td
                  className="px-4 py-2 border border-[var(--border-light)] truncate"
                  style={{ maxWidth: '5rem' }}
                  title={`${detail.grossWeight ?? ''}`}
                >
                  {detail.grossWeight}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Order Number */}
      <div className="grid grid-cols-6 mb-4 px-2">
        <div className="place-self-center ">
          <img
            src="/assets/icons/orders/jobDetails/container_number.svg"
            alt="order_number"
            className="theme-icon mx-auto"
          />
        </div>
        <div className="col-span-5">
          <p className="text-sm text-[var(--text-muted)]">
            {t('orders_page.order_number')}
          </p>
          <p
            className={`text-sm ${Order?.orderNumber &&
              Order.orderNumber !== 'N/A' &&
              !/^0+$/.test(Order.orderNumber)
              ? ''
              : 'text-[var(--text-muted)] opacity-70'
              }`}
          >
            {Order?.orderNumber &&
              Order.orderNumber !== 'N/A' &&
              !/^0+$/.test(Order.orderNumber)
              ? Order.orderNumber
              : `${t('order_details.data_not_provided')}`}
          </p>
        </div>
      </div>
      <hr className="opacity-10" />
      {/* ============= TRANSPORT DETAILS ============= */}
      <div className="flex flex-row my-5 ps-2 items-center">
        <div className="flex gap-4 ml-4">
          <img
            src="/assets/icons/orders/jobDetails/transport.svg"
            alt="transport"
            className="theme-icon mx-auto"
          />
          <div className="">
            <p className="text-base font-bold ">
              {t('order_details.transport_details')}
            </p>
          </div>
        </div>
        {userType === 'shipper' && Order.status === 'draft' && (
          <div className="mr-4 ml-auto">
            <Button
              variant="solid"
              type="button"
              size="sm"
              className="rounded-lg text-white shadow-lg font-semibold text-small"
              style={{ backgroundColor: 'var(--color-primary)' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
              onClick={openModal}
              startContent={
                <img
                  src="/assets/icons/orders/jobDetails/edit.svg"
                  alt="edit"
                  className="theme-icon w-4"
                />
              }
            >
              {t('button.assign')}
            </Button>
          </div>
        )}
      </div>
      {/* Display Selected Driver, Escort, and Vehicle */}
      <div className="grid grid-cols-6 mb-4 px-2">
        <div className="place-self-center ">
          <img
            src="/assets/icons/orders/jobDetails/user.svg"
            alt="driver"
            className="theme-icon mx-auto w-8"
          />
        </div>
        <div className="col-span-5">
          <p className="text-sm text-[var(--text-muted)]">{t('order_details.driver')}</p>
          <p>
            {Order?.driver
              ? `${Order.driver?.firstName} ${Order.driver?.lastName}`
              : `${t('order_details.not_selected')}`}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-6 mb-4 px-2">
        <div className="place-self-center ">
          <img
            src="/assets/icons/orders/jobDetails/user.svg"
            alt="escort"
            className="theme-icon mx-auto w-8"
          />
        </div>
        <div className="col-span-5">
          <p className="text-sm text-[var(--text-muted)]">{t('order_details.escort')}</p>
          <p>
            {Order?.escort
              ? `${Order.escort?.firstName} ${Order.escort?.lastName}`
              : `${t('order_details.not_selected')}`}
          </p>
        </div>
      </div>
      {Order?.escort?.phone && (
        <div className="grid grid-cols-6 mb-4 px-2">
          <div className="place-self-center ">
            <img
              src="/assets/icons/orders/jobDetails/phone.svg"
              alt="phone"
              className="theme-icon mx-auto"
            />
          </div>
          <div className="col-span-5">
            <p className="text-sm text-[var(--text-muted)]">
              {t('order_details.contact_number')}
            </p>
            <p
              className={`text-sm ${Order.escort?.phone ? '' : 'text-[var(--text-muted)] opacity-70'}`}
            >
              {Order.escort?.phone || `${t('order_details.data_not_provided')}`}
            </p>
          </div>
        </div>
      )}
      {/* Display Selected Vehicle */}
      <div className="grid grid-cols-6 mb-4 px-2">
        <div className="place-self-center ">
          <img
            src="/assets/icons/orders/jobDetails/truck.svg"
            alt="truck"
            className="theme-icon mx-auto"
          />
        </div>
        <div className="col-span-5">
          <p className="text-sm text-[var(--text-muted)]">
            {t('order_details.truck_number')}
          </p>
          <p
            className={`text-sm ${Order?.vehicle?.plateNumber ? '' : 'text-[var(--text-muted)] opacity-70'
              }`}
          >
            {Order?.vehicle?.plateNumber
              ? // If order.vehicle.plateNumber exists, display that
              (Order.vehicle?.plate?.emirates
                ? `${UAEStatesMap[
                  Order.vehicle?.plate
                    .emirates as keyof typeof UAEStatesMap
                ]?.code || ''
                }-`
                : '') + Order.vehicle?.plateNumber?.replace('private-', '')
              : // Lastly, show fallback text
              `${t('order_details.data_not_provided')}`}
          </p>
        </div>
      </div>
      {/* Unlock Order Button */}
      {Order?.status === IN_TRANSIT && user.type === 'enforcement' && (
        <div
          className={`absolute bottom-0 w-[25vw] px-8 pt-3 pb-4 shadow-outlineTop bg-[var(--bg-surface)] text-[var(--text-main)] z-0`}
        >
          <button
            className="bg-[var(--color-primary)] text-white rounded-lg p-3 w-full col-span-3"
            onClick={() => setShowUnlock(true)}
          >
            Unlock order
          </button>
        </div>
      )}
      {/* Unlock Order Modal */}
      <Modal
        show={showUnlock}
        onClose={() => setShowUnlock(false)}
        align="center"
        className="rounded-lg bg-[var(--bg-surface)] text-[var(--text-main)] mx-auto w-[25%] top-10 left-1/2 transform -translate-x-1/2"
      >
        <div className="p-6 text-center text-white">
          <img
            src={`/assets/icons/inventory/lock.svg`}
            alt="lock"
            className="theme-icon mx-auto mb-3"
          />
          <h3>You’re about to unlock the order</h3>
          <p className="text-[var(--text-muted)] text-sm mb-6">
            Are you sure you want to unlock the order? Once the order is
            unlocked, it can’t be locked again.
          </p>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <button
              className="p-3 border rounded-lg " style={{ borderColor: "var(--border-light)", color: "var(--text-main)" }}
              onClick={() => setShowUnlock(false)}
            >
              Cancel
            </button>
            <button
              className="p-3 text-white rounded-lg bg-[var(--color-primary)] "
              onClick={handleUnlockOrder}
            >
              Unlock order
            </button>
          </div>
        </div>
      </Modal>
      {/* Edit Modal */}
      <NextModal
        isOpen={isModalOpen}
        onClose={closeModal}
        isDismissable={false}
        backdrop="blur"
        className="bg-[var(--bg-surface)] text-[var(--text-main)] z-[200]"
        classNames={{
          wrapper: 'z-[200]',
          backdrop: 'z-[200]',
          closeButton:
            'bg-[var(--bg-overlay)] m-1 hover:bg-[var(--bg-surface-2)] active:bg-[var(--bg-overlay)] text-[var(--text-main)] p-2 z-[201] focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
          body: 'w-full item-center',
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="text-[var(--text-main)] opacity-90 text-2xl font-semibold my-2">
                {t('order_details.update_transport_details')}
              </ModalHeader>
              <ModalBody>
                <div id="transport-modal-container" className="flex flex-col py-6 gap-10">
                  {/* Driver Selection */}
                  <Select
                    variant="flat"

                    radius="md"
                    size="lg"
                    className="w-full"
                    labelPlacement="outside"
                    label={t('order_details.driver')}
                    placeholder="Select a driver"
                    classNames={{
                      listbox: 'p-2 shadow-lg bg-[var(--bg-surface-2)]',
                      label: 'text-medium font-semibold text-[var(--text-main)] top-4',
                      trigger:
                        'bg-[var(--bg-surface-2)] border border-[var(--border-light)] !py-2 !min-h-[56px] !items-center shadow-md data-[hover=true]:bg-[var(--bg-overlay)] data-[hover=true]:shadow-lg',
                      value: '!items-center',
                      listboxWrapper: 'bg-[var(--bg-surface)] border border-[var(--border-light)] rounded-md',
                      popoverContent:
                        'rounded-sm bg-[var(--bg-surface)] text-[var(--text-main)] shadow-md z-[300]',
                    }}
                    popoverProps={{
                      className: 'z-[300]',
                    }}
                    selectedKeys={
                      selectedDriver ? new Set([selectedDriver._id]) : new Set()
                    }
                    onSelectionChange={(keys) => {
                      const selectedKey = Array.from(keys)[0];
                      const matchedDriver = Order.possibleDrivers.find(
                        (d: Driver) => d._id === selectedKey
                      );
                      setSelectedDriver(matchedDriver);
                    }}
                    items={Order.possibleDrivers}
                    renderValue={(items) =>
                      items.map((item) => (
                        <div key={item.key} className="flex items-center gap-2">
                          <Avatar
                            alt={item?.data?.firstName}
                            className="flex-shrink-0 bg-[var(--bg-surface-2)] text-[var(--text-main)] p-1 border border-[var(--border-light)]"
                            size="md"
                            src={'/assets/icons/orders/jobDetails/user.svg'}
                            classNames={{ img: 'theme-icon' }} />
                          <div className="flex flex-col text-[var(--text-main)]">
                            <span>
                              {item?.data?.firstName} {item?.data?.lastName}
                            </span>
                            <span className="text-default-500 text-tiny">
                              {item?.data?.dlNumber}
                            </span>
                          </div>
                        </div>
                      ))
                    }
                  >
                    {(driver: Driver) => (
                      <SelectItem
                        key={driver._id}
                        textValue={`${driver.firstName} ${driver.lastName}`}
                        classNames={{
                          base: 'my-1 text-lg data-[selected=true]:bg-[var(--color-primary-light)] data-[selected=true]:border data-[selected=true]:border-[var(--color-primary)] rounded-lg outline-none data-[focus-visible=true]:outline-none data-[focus-visible=true]:ring-0',
                          wrapper: 'gap-10',
                          selectedIcon: 'text-[var(--color-primary)] w-4 h-4',
                        }}
                      >
                        <div className="flex gap-4 items-center">
                          <Avatar
                            alt={driver.firstName}
                            className="flex-shrink-0 bg-[var(--bg-surface-2)] text-[var(--text-main)] p-1 border border-[var(--border-light)] w-10 h-10"
                            size="md"
                            src={'/assets/icons/orders/jobDetails/user.svg'}
                            classNames={{ img: 'theme-icon' }} />
                          <div className="flex flex-col">
                            <span className="text-medium">{`${driver.firstName} ${driver.lastName}`}</span>
                            <span className="text-small text-default-400">
                              {driver.dlNumber}
                            </span>
                          </div>
                        </div>
                      </SelectItem>
                    )}
                  </Select>

                  {/* Escort Selection */}
                  <Select
                    variant="flat"

                    radius="md"
                    size="lg"
                    className="w-full"
                    labelPlacement="outside"
                    label={t('order_details.escort')}
                    placeholder="Select an escort"
                    classNames={{
                      listbox: 'p-2 shadow-lg bg-[var(--bg-surface-2)]',
                      label: 'text-medium font-semibold text-[var(--text-main)] top-4',
                      trigger:
                        'bg-[var(--bg-surface-2)] border border-[var(--border-light)] !py-2 !min-h-[56px] !items-center shadow-md data-[hover=true]:bg-[var(--bg-overlay)] data-[hover=true]:shadow-lg',
                      value: '!items-center',
                      listboxWrapper: 'bg-[var(--bg-surface)] border border-[var(--border-light)] rounded-md',
                      popoverContent:
                        'rounded-sm bg-[var(--bg-surface)] text-[var(--text-main)] shadow-md z-[300]',
                    }}
                    popoverProps={{
                      className: 'z-[300]',
                    }}
                    selectedKeys={
                      selectedEscort ? new Set([selectedEscort._id]) : new Set()
                    }
                    onSelectionChange={(keys) => {
                      const selectedKey = Array.from(keys)[0];
                      const matchedEscort = Order.possibleEscorts.find(
                        (e: Escort) => e._id === selectedKey
                      );
                      setSelectedEscort(matchedEscort);
                    }}
                    items={Order.possibleEscorts}
                    renderValue={(items) =>
                      items.map((item) => (
                        <div key={item.key} className="flex items-center gap-2">
                          <Avatar
                            alt={item?.data?.firstName}
                            className="flex-shrink-0 bg-[var(--bg-surface-2)] text-[var(--text-main)] p-1 border border-[var(--border-light)]"
                            size="md"
                            src={'/assets/icons/orders/jobDetails/user.svg'}
                            classNames={{ img: 'theme-icon' }} />
                          <div className="flex flex-col text-[var(--text-main)]">
                            <span>
                              {item?.data?.firstName} {item?.data?.lastName}
                            </span>
                            <span className="text-default-500 text-tiny">
                              {item?.data?.siraId}
                            </span>
                          </div>
                        </div>
                      ))
                    }
                  >
                    {(escort: Escort) => (
                      <SelectItem

                        key={escort._id}
                        textValue={`${escort.firstName} ${escort.lastName}`}
                        classNames={{
                          base: 'my-1 text-lg data-[selected=true]:bg-[var(--color-primary-light)] data-[selected=true]:border data-[selected=true]:border-[var(--color-primary)] rounded-lg outline-none data-[focus-visible=true]:outline-none data-[focus-visible=true]:ring-0',
                          wrapper: 'gap-10',
                          selectedIcon: 'text-[var(--color-primary)] w-4 h-4',
                        }}
                      >
                        <div className="flex gap-4 items-center">
                          <Avatar
                            alt={escort.firstName}
                            className="flex-shrink-0 bg-[var(--bg-surface-2)] text-[var(--text-main)] p-1 border border-[var(--border-light)] w-10 h-10"
                            size="md"
                            src={'/assets/icons/orders/jobDetails/user.svg'}
                            classNames={{ img: 'theme-icon' }} />
                          <div className="flex flex-col">
                            <span className="text-medium">{`${escort.firstName} ${escort.lastName}`}</span>
                            <span className="text-small text-default-400">
                              {escort.siraId}
                            </span>
                          </div>
                        </div>
                      </SelectItem>
                    )}
                  </Select>

                  {/* Vehicle Selection */}
                  <Select
                    variant="flat"

                    radius="md"
                    size="lg"
                    className="w-full"
                    labelPlacement="outside"
                    label={t('order_details.vehicle')}
                    placeholder="Select a vehicle"
                    classNames={{
                      listbox: 'p-2 shadow-lg bg-[var(--bg-surface-2)]',
                      label: 'text-medium font-semibold text-[var(--text-main)] top-4',
                      trigger:
                        'bg-[var(--bg-surface-2)] border border-[var(--border-light)] !py-2 !min-h-[56px] !items-center shadow-md data-[hover=true]:bg-[var(--bg-overlay)] data-[hover=true]:shadow-lg',
                      value: '!items-center',
                      listboxWrapper: 'bg-[var(--bg-surface)] border border-[var(--border-light)] rounded-md',
                      popoverContent:
                        'rounded-sm bg-[var(--bg-surface)] text-[var(--text-main)] shadow-md z-[300]',
                    }}
                    popoverProps={{
                      className: 'z-[300]',
                    }}
                    selectedKeys={
                      selectedVehicle
                        ? new Set([selectedVehicle._id])
                        : new Set()
                    }
                    onSelectionChange={(keys) => {
                      const selectedKey = Array.from(keys)[0];
                      const matchedVehicle = Order.possibleVehicles.find(
                        (v: Vehicle) => v._id === selectedKey
                      );
                      setSelectedVehicle(matchedVehicle);
                    }}
                    items={Order.possibleVehicles}
                    renderValue={(items) =>
                      items.map((item) => (
                        <div key={item.key} className="flex items-center gap-2">
                          <Avatar
                            alt={item?.data?.plateNumber}
                            className="flex-shrink-0 bg-[var(--bg-surface)] text-[var(--text-main)] p-2"
                            size="md"
                            src={'/assets/icons/orders/jobDetails/truck.svg'}
                            classNames={{ img: 'theme-icon' }} />
                          <div className="flex flex-col text-[var(--text-main)]">
                            <span className="text-medium">
                              {item?.data?.plateNumber
                                ? (item?.data?.plate?.emirates
                                  ? `${UAEStatesMap[
                                    item?.data?.plate
                                      .emirates as keyof typeof UAEStatesMap
                                  ]?.code || ''
                                  }-`
                                  : '') +
                                item?.data?.plateNumber.replace(
                                  'private-',
                                  ''
                                )
                                : `${t('order_details.data_not_provided')}`}
                            </span>
                          </div>
                        </div>
                      ))
                    }
                  >
                    {(vehicle: Vehicle) => (
                      <SelectItem

                        key={vehicle._id}
                        textValue={`${vehicle.plateNumber}`}
                        classNames={{
                          base: 'my-1 text-lg data-[selected=true]:bg-[var(--color-primary-light)] data-[selected=true]:border data-[selected=true]:border-[var(--color-primary)] rounded-lg outline-none data-[focus-visible=true]:outline-none data-[focus-visible=true]:ring-0',
                          wrapper: 'gap-10',
                          selectedIcon: 'text-[var(--color-primary)] w-4 h-4',
                        }}
                      >
                        <div className="flex gap-4 items-center">
                          <Avatar
                            alt={vehicle.plateNumber}
                            className="flex-shrink-0 bg-[var(--bg-surface)] text-[var(--text-main)] p-2"
                            size="md"
                            src={'/assets/icons/orders/jobDetails/truck.svg'}
                            classNames={{ img: 'theme-icon' }} />
                          <div className="flex flex-col">
                            <span className="text-medium">
                              {vehicle?.plateNumber
                                ? (vehicle?.plate?.emirates
                                  ? `${UAEStatesMap[
                                    vehicle?.plate
                                      .emirates as keyof typeof UAEStatesMap
                                  ]?.code || ''
                                  }-`
                                  : '') +
                                vehicle?.plateNumber?.replace('private-', '')
                                : `${t('order_details.data_not_provided')}`}
                            </span>
                          </div>
                        </div>
                      </SelectItem>
                    )}
                  </Select>
                </div>
              </ModalBody>
              <ModalFooter className="m-auto my-4">
                <Button
                  onPress={handleConfirmTransportDetails}
                  className="rounded-lg shadow-md hover:shadow-lg font-semibold text-medium transition-colors"
                  style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-invert)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-primary)')}
                  startContent={
                    <img
                      src="/assets/icons/orders/jobDetails/tick_circle.svg"
                      alt="tick"
                      className="theme-icon w-5"
                    />
                  }
                >
                  {t('order_details.confirm')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </NextModal>

      <CustomSnackbar
        open={openSnackbar}
        handleClose={() => setOpenSnackbar(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'end' }}
      />
    </div>
  );
}
